import { useCallback } from 'react'

import { Button } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { usePermission } from '@circlefin/permissions/react'
import { USER_MANAGEMENT_MUTATION } from '@services/permissions'
import { routes } from '@services/sections/modal'
import { SegmentEvents, useSegment } from '@services/segment'
import { UserStatusType, type User } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

export interface UsersTableInviteActionsProps {
  /**
   * The user object for the row to represent.
   */
  user: User
}

export const UsersTableInviteActions: React.FC<
  UsersTableInviteActionsProps
> = ({ user }) => {
  const { t } = useTranslation('settings')
  const modal = useModal()
  const { track } = useSegment()
  const [UserManagementMutation] = usePermission(USER_MANAGEMENT_MUTATION)

  const handleCancelInvite = useCallback(() => {
    track(SegmentEvents.CancelInviteClicked)
    modal.router.push({
      pathname: routes.account.users.remove,
      query: {
        user,
      },
    })
  }, [modal.router, user, track])

  const handleResendInvite = useCallback(
    () =>
      modal.router.push({
        pathname: routes.account.users.resendInvite,
        query: {
          user,
        },
      }),
    [modal.router, user],
  )

  if (
    ![UserStatusType.PENDING, UserStatusType.INVITE_EXPIRED].includes(
      user.status,
    )
  ) {
    return null
  }

  return (
    <div className="flex flex-row gap-x-3">
      <UserManagementMutation.Authorized>
        <Button
          className="-ml-1"
          onClick={handleResendInvite}
          size="sm"
          variant="text"
        >
          {t('personalInfo.resendInvite')}
        </Button>
      </UserManagementMutation.Authorized>
      <Button
        className="-ml-1"
        onClick={handleCancelInvite}
        size="sm"
        variant="text"
      >
        {t('personalInfo.cancelInvite')}
      </Button>
    </div>
  )
}
